import React, { useState, useEffect } from 'react';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { IconWithBackground } from '../components/ui/icon-with-background';
import BunnyLoader from '../components/ui/BunnyLoader';
import { 
  FeatherCalendar, 
  FeatherPlus,
  FeatherEdit2,
  FeatherTrash2,
  FeatherChevronLeft,
  FeatherChevronRight
} from '../components/ui/icons';
import { holidayApi } from '../services/holidayService';
import { userApi } from '../services/api';
import { isManager } from '../utils/roleUtils';
import HolidayForm from '../components/Holidays/HolidayForm';
import './HolidayCalendar.css';

const HolidayCalendar = () => {
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [error, setError] = useState(null);
  const [userIsManager, setUserIsManager] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  useEffect(() => {
    checkUserRole();
    fetchHolidays();
  }, [selectedYear]);

  const checkUserRole = async () => {
    try {
      const { role } = await userApi.getRole();
      setUserIsManager(isManager(role));
    } catch (err) {
      console.error('Error checking user role:', err);
    }
  };

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const data = await holidayApi.getAll(selectedYear);
      setHolidays(data.holidays || []);
      setError(null);
    } catch (err) {
      setError('Failed to load holidays');
      console.error('Error fetching holidays:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHoliday = () => {
    setEditingHoliday(null);
    setShowForm(true);
  };

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setShowForm(true);
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }

    try {
      await holidayApi.delete(holidayId);
      await fetchHolidays();
    } catch (err) {
      setError('Failed to delete holiday');
      console.error('Error deleting holiday:', err);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingHoliday(null);
    fetchHolidays();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingHoliday(null);
  };

  const navigateYear = (direction) => {
    setSelectedYear(prev => prev + direction);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  };

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="loading-container">
          <BunnyLoader 
            size="large" 
            text="Loading holiday calendar..."
            animation="bounce"
          />
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="holiday-calendar-container">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="flex items-center gap-3">
              <IconWithBackground 
                icon={<FeatherCalendar />} 
                variant="brand"
              />
              <h1 className="text-heading-2">Holiday Calendar</h1>
            </div>
          </div>
          {userIsManager && (
            <div className="header-right">
              <Button
                variant="primary"
                icon={<FeatherPlus />}
                onClick={handleCreateHoliday}
              >
                Create Holiday
              </Button>
            </div>
          )}
        </div>

        {/* Year Selector */}
        <div className="year-selector">
          <Button
            variant="neutral-tertiary"
            icon={<FeatherChevronLeft />}
            onClick={() => navigateYear(-1)}
          >
            Previous
          </Button>
          <div className="year-display">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="year-select"
            >
              {getYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <Button
            variant="neutral-tertiary"
            icon={<FeatherChevronRight />}
            onClick={() => navigateYear(1)}
          >
            Next
          </Button>
        </div>

        {error && (
          <div className="error-message">
            <div className="error">{error}</div>
          </div>
        )}

        {/* Holidays List */}
        <div className="holidays-list">
          {holidays.length === 0 ? (
            <div className="empty-state">
              <IconWithBackground 
                icon={<FeatherCalendar />} 
                variant="neutral"
              />
              <h3>No Holidays</h3>
              <p>No holidays found for {selectedYear}</p>
              {userIsManager && (
                <Button
                  variant="primary"
                  icon={<FeatherPlus />}
                  onClick={handleCreateHoliday}
                >
                  Create First Holiday
                </Button>
              )}
            </div>
          ) : (
            holidays.map((holiday) => (
              <div key={holiday.id} className="holiday-card">
                <div className="holiday-header">
                  <div className="holiday-info">
                    <h3 className="holiday-name">{holiday.name}</h3>
                    <span className="holiday-date">{formatDate(holiday.date)}</span>
                  </div>
                  <div className="holiday-badges">
                    {holiday.is_restricted && (
                      <Badge variant="warning">Restricted</Badge>
                    )}
                  </div>
                </div>
                {holiday.description && (
                  <p className="holiday-description">{holiday.description}</p>
                )}
                {userIsManager && (
                  <div className="holiday-actions">
                    <Button
                      variant="neutral-tertiary"
                      icon={<FeatherEdit2 />}
                      onClick={() => handleEditHoliday(holiday)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="neutral-tertiary"
                      icon={<FeatherTrash2 />}
                      onClick={() => handleDeleteHoliday(holiday.id)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Holiday Form Modal */}
        {showForm && (
          <HolidayForm
            holiday={editingHoliday}
            year={selectedYear}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        )}
      </div>
    </DefaultPageLayout>
  );
};

export default HolidayCalendar;

